import { useMemo, useState } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mesh.ai/rabbit';
import { RRule } from 'rrule';
import { dayCapitalizedRRuleString } from 'screens/oneOnOneRabbit/InstancePage/utils';

import { CustomRecurrence } from './CustomRecurrence';
import { generateFrequencyOptions } from './helper';

type RecurrenceSelectProps = {
  date: Date;
  recurrenceRule: string;
  onRecurrenceChange: (recurrence: string) => void;
};

export const RecurrenceSelect = ({ date, recurrenceRule, onRecurrenceChange }: RecurrenceSelectProps) => {
  const [isCustomRecurrenceModalOpen, setIsCustomRecurrenceModalOpen] = useState(false);

  const handleSelectChange = (value: string) => {
    if (value === 'Custom') {
      setIsCustomRecurrenceModalOpen(true);
      return;
    }

    onRecurrenceChange(value);
  };

  const frequencyOptions = useMemo(() => {
    const options = generateFrequencyOptions(date); // Base options

    // Add current recurrenceRule if not already in options
    const selectedFrequencyIsNotInOptions = !options.some((opt) => opt.value === recurrenceRule);
    if (recurrenceRule && selectedFrequencyIsNotInOptions) {
      try {
        const rruleObj = RRule.fromString(recurrenceRule);
        const value = rruleObj.toString().split('RRULE:')[1];

        options.push({
          label: dayCapitalizedRRuleString(rruleObj.toText()),
          value,
        });
      } catch (error) {
        console.error('Error parsing recurrence rule:', error);
      }
    }

    const CUSTOM_OPTION = { label: 'Custom', value: 'Custom' };

    // Add the Custom option
    return [...options, CUSTOM_OPTION];
  }, [date, recurrenceRule]);

  return (
    <>
      <Select value={recurrenceRule} onValueChange={handleSelectChange}>
        <SelectTrigger style={{ width: '300px' }}>
          <SelectValue placeholder="Select Recurrence" />
        </SelectTrigger>
        <SelectContent>
          {frequencyOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isCustomRecurrenceModalOpen && (
        <CustomRecurrence
          isOpen={isCustomRecurrenceModalOpen}
          onClose={() => setIsCustomRecurrenceModalOpen(false)}
          onConfirm={(recurrence) => {
            onRecurrenceChange(recurrence);
            setIsCustomRecurrenceModalOpen(false);
          }}
          date={date}
        />
      )}
    </>
  );
};
